import * as React from 'react';
import * as Sebrae from '@sebrae/react-core';
import {
  BaSeContainer,
  BaSeDataRowInfoConsumer,
  BaSeDataRowInfoContext,
  BaSeDataTable,
  BaSeDialogContext,
  BaSeHeading5,
  BaSeInput,
  BaSeParagraph,
  BaSeShapeButton,
  BaSeTheme,
} from '@base/react';
import { DataTableWrapper } from '../../pages/produtos/produtos-styled';
import moment from 'moment';
import filesize from 'filesize';
import {
  CERTIFICADO_ANEXO,
  CERTIFICADO_ANEXOS,
  PRODUTO_ENDPOINT,
} from '../../hooks/constants';
import { Role } from '../../models/role';
import { DeleteContent } from '../product/modal/delete';
import { avaliableActions } from '../../models/actions';
import { ContentGrid } from './gestao-arquivos-styled';
import { useFilesService } from '../../hooks/arquivos-service';
import { ReactComponent as ImgFileIcon } from '../../assets/img-arquivo.svg';

let paramsUpdate = {
  nome: '',
};

const salvar = async (fetchData, notify, endpointComponent, id, backendPut) => {
  try {
    await backendPut(`${endpointComponent}/${id}`, paramsUpdate);
    fetchData({
      sort: { field: 'dataAtualizacao', type: 'DESC' },
    });
    notify({
      message: 'Dados salvos com sucesso!',
      color: 'confirmation',
    });
    return { sucess: true };
  } catch (error) {
    fetchData({
      sort: { field: 'dataAtualizacao', type: 'DESC' },
    });
    notify({
      message: 'Erro ao salvar dados',
      color: 'destructive',
      supportMessage: error.message,
    });
    return { ...error };
  }
};

const EditableRowItem = ({
  value,
  label,
  notify,
  fetchData,
  backendPut,
  id,
  endpointComponent,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const [showInput, setShowInput] = React.useState(false);
  const [input, setInput] = React.useState(value);

  const { rowInfo } = React.useContext(BaSeDataRowInfoContext);

  React.useEffect(() => {
    setShowInput(
      [`editing:${label}`.toLowerCase(), 'editing:all'].includes(rowInfo),
    );
  }, [label, rowInfo, value]);

  React.useEffect(() => {
    if (rowInfo === 'save') {
      salvar(fetchData, notify, endpointComponent, id, backendPut);
    }
  }, [backendPut, endpointComponent, fetchData, id, input, rowInfo, notify]);

  React.useEffect(() => {
    if (rowInfo === 'abort') {
      setInput(value);
    }
  }, [rowInfo, value]);

  React.useEffect(() => {
    if (rowInfo === 'editing:all') {
      paramsUpdate.nome = input;
    }
  }, [input, rowInfo]);

  return showInput ? (
    <BaSeInput
      size="small"
      placeholder={label}
      value={input}
      onChange={setInput}
    />
  ) : (
    <BaSeParagraph color={BaSeTheme.colors.institucionais.cinzaSebrae30}>
      {input || value}
    </BaSeParagraph>
  );
};

const EditActions = ({
  values,
  reloadData,
  isAction,
  endpointComponent,
  isTemplateCertificados,
}) => {
  const [editing, setEditing] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const { hasRoles } = Sebrae.useAuth();
  const { backendDelete } = Sebrae.useBackend();
  const { showNotification, showModal } = React.useContext(BaSeDialogContext);

  const copyLink = React.useCallback(() => {
    navigator.clipboard.writeText(values.urlStorage);
    showNotification({
      message: 'Link copiado com sucesso!',
      color: 'confirmation',
      secondsToClose: 4,
    });
  }, [showNotification, values.urlStorage]);

  const handleShowFile = React.useCallback(() => {
    const random = new Date().getTime();
    const win = window.open(values.urlStorage + `?time=${random}`, '_blank');
    win.focus();
  }, [values.urlStorage]);

  // const downloadFile = React.useCallback(() => {
  //   setLoading(true);
  // backendGetBlob(
  //   `${PRODUTO_ENDPOINT}/${values.codigoProduto}/anexo/${values.id}/arquivo`,
  // )
  //     .then((blob) => {
  //       download(blob, values.nome, blob.type);
  //       showNotification({
  //         message: 'Download realizado com sucesso!',
  //         color: 'confirmation',
  //       });
  //     })
  //     .catch((error) => {
  //       showNotification({
  //         message: `Erro ao efetuar download do arquivo`,
  //         color: 'destructive',
  //       });
  //     })
  //     .finally(() => setLoading(false));
  // }, [backendGetBlob, showNotification, values]);

  const handleDeleteAction = React.useCallback(
    (action) => {
      if (action === avaliableActions.confirmar) {
        setLoading(true)
        backendDelete(`${endpointComponent}/${values.id}`)
          .then(() => {
            showNotification({
              message: 'Arquivo excluído com sucesso!',
              color: 'confirmation',
            });
            reloadData();
          })
          .catch((e) =>
            showNotification({
              message: 'Arquivo não pode ser excluído',
              supportMessage: e?.message ?? '',
              color: 'destructive',
            }),
          )
          .finally(() => setLoading(false))
      }
      return true;
    },
    [backendDelete, endpointComponent, reloadData, showNotification, values],
  );

  const productsModalConfigs = React.useCallback(
    (arquivo, func, title, actions, Content, size) => ({
      size,
      title,
      actions,
      onChooseAction: (action) => func?.(action, arquivo),
      content: () => Content,
    }),
    [],
  );

  const handleShowDeleteModal = React.useCallback(
    (values) => {
      const description = [
        'Tem certeza que deseja excluir esse arquivo?',
        'Não será possível recuperar.',
      ];

      return productsModalConfigs(
        values,
        handleDeleteAction,
        'Atenção!',
        [
          avaliableActions.confirmar,
          {
            action: avaliableActions.desistir,
            type: 'secondary',
            color: BaSeTheme.colors.institucionais.azulSebrae36,
          },
        ],
        <DeleteContent texts={description} />,
        'medium',
      );
    },
    [handleDeleteAction, productsModalConfigs],
  );

  const handleUpdate = React.useCallback((action, func) => {
    if (action === avaliableActions.alterar) {
      func('save');
    }
    return true;
  }, []);

  const handleUpdateModal = React.useCallback(
    (func) => {
      const description = [
        'Tem certeza que deseja alterar esse arquivo?',
        'Qualquer alteração deverá ser ajustada no template de certificado!',
      ];

      return {
        size: 'large',
        title: 'Atenção!',
        actions: [
          avaliableActions.alterar,
          {
            action: avaliableActions.desistirAlterar,
            type: 'secondary',
            color: BaSeTheme.colors.institucionais.azulSebrae36,
          },
        ],
        onChooseAction: (action) => handleUpdate(action, func),
        content: () => <DeleteContent texts={description} />,
      };
    },
    [handleUpdate],
  );

  return (
    <BaSeDataRowInfoConsumer>
      {
        // eslint-disable-next-line sonarjs/cognitive-complexity
        ({ setRowInfo }) =>
          editing ? (
            <>
              <BaSeShapeButton
                size="small"
                onClick={() => {
                  setEditing(false);
                  setRowInfo(() => {
                    if (isTemplateCertificados) {
                      showModal(handleUpdateModal(setRowInfo));
                      return 'showModal';
                    }
                    return 'save';
                  });
                }}
                nameIcon="check"
                type="primary"
                sizeIcon="small"
                shape="square"
                color="confirmation"
                tooltip={{
                  message: 'Salva',
                  direction: 'top',
                }}
              />
              <BaSeShapeButton
                size="small"
                onClick={() => {
                  setRowInfo('abort');
                  setEditing(false);
                }}
                nameIcon="close"
                type="primary"
                sizeIcon="small"
                shape="square"
                color="destructive"
                tooltip={{
                  message: 'Cancela',
                  direction: 'top',
                }}
              />
            </>
          ) : (
            hasRoles([Role.Administrador]) && (
              <>
                {!isAction ? (
                  <>
                    <BaSeShapeButton
                      isLoading={loading}
                      size="small"
                      onClick={() => handleShowFile()}
                      nameIcon="eye"
                      type="tertiary"
                      sizeIcon="small"
                      shape="square"
                      tooltip={{
                        direction: 'top',
                        message: 'Visualizar',
                      }}
                    />
                    <BaSeShapeButton
                      size="small"
                      onClick={() => {
                        setRowInfo('editing:all');
                        setEditing(true);
                      }}
                      nameIcon="edit-alt"
                      type="tertiary"
                      sizeIcon="small"
                      shape="square"
                      tooltip={{
                        direction: 'top',
                        message: 'Editar',
                      }}
                    />
                    <BaSeShapeButton
                      isLoading={loading }
                      size="small"
                      onClick={() => {
                        showModal(handleShowDeleteModal());
                      }}
                      nameIcon="trash-alt"
                      type="tertiary"
                      sizeIcon="small"
                      shape="square"
                      tooltip={{
                        direction: 'top',
                        message: 'Remover',
                      }}
                    />
                    <BaSeShapeButton
                      size="small"
                      onClick={() => copyLink()}
                      nameIcon="link"
                      type="tertiary"
                      sizeIcon="small"
                      shape="square"
                      tooltip={{
                        direction: 'top',
                        message: 'Copiar link',
                      }}
                    />
                  </>
                ) : (
                  ''
                )}
              </>
            )
          )
      }
    </BaSeDataRowInfoConsumer>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const GestaoArquivos = ({
  codigoProduto,
  isTemplateCertificados = false,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [isAction, setIsAction] = React.useState(false);

  const endpointComponent = React.useMemo(
    () =>
      isTemplateCertificados
        ? CERTIFICADO_ANEXOS
        : ` ${PRODUTO_ENDPOINT}/${codigoProduto}/anexos`,
    [codigoProduto, isTemplateCertificados],
  );

  const { deleteSelectedItems, downloadListaNomes, downloadListaLinks } =
    useFilesService();
  const { hasRoles } = Sebrae.useAuth();
  const { backendPut } = Sebrae.useBackend();
  const { showNotification, showModal } = React.useContext(BaSeDialogContext);
  const { fetchData, data, loading } = Sebrae.usePagination(
    `${endpointComponent}/grid`,
  );

  const { azulSebrae, cinzaSebrae45 } = BaSeTheme.colors.institucionais;

  const endpointAnexo = React.useMemo(
    () =>
      isTemplateCertificados
        ? CERTIFICADO_ANEXO
        : ` ${PRODUTO_ENDPOINT}/${codigoProduto}/anexo`,
    [codigoProduto, isTemplateCertificados],
  );

  const firstColumns = React.useMemo(
    () => [
      {
        accessorKey: 'nome',
        header: 'Nome do arquivo',
        mapper: ({ values }) => {
          return (
            <EditableRowItem
              value={values.nomeSemExtensao ?? values.nome}
              label="Nome"
              fetchData={fetchData}
              backendPut={backendPut}
              endpointComponent={endpointAnexo}
              notify={showNotification}
              id={values.id}
            />
          );
        },
      },
    ],
    [backendPut, fetchData, showNotification, endpointAnexo],
  );

  const lastColumns = React.useMemo(
    () => [
      {
        accessorKey: 'extensao',
        header: 'Tipo de arquivo',
        mapper: ({ values: { extensao } }) => extensao.toUpperCase(),
      },
      {
        accessorKey: 'tamanho',
        header: 'Tamanho do arquivo',
        mapper: ({ values: { tamanho } }) => filesize(tamanho),
      },
      {
        accessorKey: 'dataAtualizacao',
        header: 'Data da última modificação',
        mapper: ({ values: { dataAtualizacao } }) => {
          return `${moment(dataAtualizacao).format('DD/MM/YYYY')} às ${moment(
            dataAtualizacao,
          ).format('HH:mm')}`;
        },
      },
      {
        header: hasRoles([Role.Administrador]) && 'Ações',
        id: 'acoes',
        mapper: ({ values, reloadData }) => {
          return (
            <EditActions
              values={values}
              reloadData={reloadData}
              isAction={isAction}
              endpointComponent={endpointAnexo}
              isTemplateCertificados={isTemplateCertificados}
            />
          );
        },
      },
    ],
    [endpointAnexo, hasRoles, isAction, isTemplateCertificados],
  );

  const config = React.useMemo(
    () => ({
      config: [...firstColumns, ...lastColumns],
    }),
    [firstColumns, lastColumns],
  );

  const paramsSelectedItems = React.useCallback(
    (selectedItems, isAllSelected) => {
      const idsFiles = selectedItems.reduce((acc, { id }) => {
        acc.push(id);
        return acc;
      }, []);

      const files = {
        ids: isAllSelected ? [] : idsFiles,
        todos: isAllSelected ?? false,
      };

      return files;
    },
    [],
  );

  const handleDeleteSelectedItemsAction = React.useCallback(
    async (action, selectedItems, isAllSelected, onSuccess) => {
      if (action === avaliableActions.confirmar) {
        await deleteSelectedItems(
          paramsSelectedItems(selectedItems, isAllSelected),
          onSuccess,
          () => fetchData({ sort: { field: 'dataConclusao', type: 'DESC' } }),
          codigoProduto,
          isTemplateCertificados,
        );
        //
      }
      return true;
    },
    [codigoProduto, deleteSelectedItems, fetchData, isTemplateCertificados, paramsSelectedItems],
  );

  const handleDownloadListItems = React.useCallback(
    async (selectedItems, isAllSelected, onSuccess, tipo) => {
      if (tipo === 'nomes') {
        await downloadListaNomes(
          paramsSelectedItems(selectedItems, isAllSelected),
          onSuccess,
          () => setIsLoading(false),
          codigoProduto,
          isTemplateCertificados,
        );
      } else {
        await downloadListaLinks(
          paramsSelectedItems(selectedItems, isAllSelected),
          onSuccess,
          () => setIsLoading(false),
          codigoProduto,
          isTemplateCertificados,
        );
      }
      return true;
    },
    [
      codigoProduto,
      downloadListaLinks,
      downloadListaNomes,
      isTemplateCertificados,
      paramsSelectedItems,
    ],
  );

  const modalConfig = React.useCallback(
    (
      selecteItems,
      allSelected,
      reloadData,
      func,
      title,
      actions,
      Content,
      size,
    ) => ({
      size,
      title,
      actions,
      onChooseAction: (action) =>
        func?.(action, selecteItems, allSelected, reloadData),
      content: () => Content,
    }),
    [],
  );

  const handleDeleteSelectedItems = React.useCallback(
    (allSelected, selecteItems, reloadData) => {
      const pluralMessage = (selecteItems.length || data.total) > 1 ? 's' : '';

      const deleteDescription = [
        `Tem certeza que deseja excluir o${pluralMessage}${
          !!selecteItems.length
            ? pluralMessage === 's'
              ? ` ${selecteItems.length}`
              : ''
            : ` ${data.total}`
        } arquivo${pluralMessage}?`,
        'Não será possível recuperar.',
      ];

      return modalConfig(
        selecteItems,
        allSelected,
        reloadData,
        handleDeleteSelectedItemsAction,
        'Atenção!',
        [
          avaliableActions.confirmar,
          {
            action: avaliableActions.desistir,
            type: 'secondary',
            color: BaSeTheme.colors.institucionais.azulSebrae36,
          },
        ],
        <DeleteContent texts={deleteDescription} />,
        'medium',
      );
    },
    [data.total, handleDeleteSelectedItemsAction, modalConfig],
  );

  const handleNotSelected = React.useCallback(() => {
    setIsAction(false);
    return [];
  }, []);

  const handleSelectedItemsAction = React.useCallback(
    (selectedItems, isAllSelected, reloadData) => {
      setIsAction(true);
      const buttonActions = [
        {
          value: 'Baixar lista de nomes',
          color: azulSebrae,
          type: isAllSelected ? 'primary' : 'secondary',
          isLoading: isLoading,
          leftIcon: 'down-arrow',
          onClick: () => {
            handleDownloadListItems(
              selectedItems,
              isAllSelected,
              reloadData,
              'nomes',
            );
          },
        },
        {
          value: 'Baixar lista de links',
          color: azulSebrae,
          type: isAllSelected ? 'primary' : 'secondary',
          isLoading: isLoading,
          leftIcon: 'link',
          onClick: () => {
            handleDownloadListItems(
              selectedItems,
              isAllSelected,
              reloadData,
              'links',
            );
          },
        },
        {
          value: 'Remover',
          color: 'destructive',
          type: isAllSelected ? 'primary' : 'secondary',
          isLoading: isLoading,
          leftIcon: 'trash-alt',
          onClick: () => {
            showModal(
              handleDeleteSelectedItems(
                isAllSelected,
                selectedItems,
                reloadData,
              ),
            );
          },
        },
      ];

      return buttonActions;
    },
    [
      azulSebrae,
      handleDeleteSelectedItems,
      handleDownloadListItems,
      isLoading,
      showModal,
    ],
  );

  const handleNotFoundContent = React.useCallback(
    () => (
      <div style={{ display: 'grid', placeItems: 'center', gap: 8 }}>
        <ImgFileIcon style={{ marginBottom: 13 }} />
        <BaSeHeading5 color={cinzaSebrae45} isBold>
          Nenhum arquivo adicionado.
        </BaSeHeading5>
        <BaSeHeading5 color={cinzaSebrae45}>
          Clique no botão enviar arquivos para adicionar.
        </BaSeHeading5>
      </div>
    ),
    [cinzaSebrae45],
  );

  return (
    <BaSeContainer delimitedOnBreakpoint="desktop">
      <ContentGrid>
        <DataTableWrapper>
          <BaSeDataTable
            columnConfig={[
              {
                columnId: 'acoes',
                width: 115,
              },
              {
                columnId: 'extensao',
                width: 120,
              },
              {
                columnId: 'tamanho',
                width: 150,
              },
              {
                columnId: 'dataAtualizacao',
                width: 200,
              },
            ]}
            {...config}
            onTextFilter={({ setFilter, term }) => {
              setFilter({ filter: term });
            }}
            hasTextFilter
            fetchData={fetchData}
            hasHeader
            title={``}
            titleColor={cinzaSebrae45}
            hasPagination
            hasSorting
            textFilterAlwaysVisible
            initialPageIndex={0}
            initialPageSize={10}
            initialSortField="dataAtualizacao"
            initialSortType="DESC"
            singularItemDescription="arquivo"
            pluralItemDescription="arquivos"
            genderOfItemDescription="male"
            isLoading={loading}
            items={data.items}
            total={data.total}
            itemsPerPage={[10, 20, 30, 100]}
            densityLevel="normal"
            renderNotFoundContent={() => handleNotFoundContent()}
            hasSelection
            selectionButtonActions={({
              isAllSelected,
              selectedItems,
              reloadData,
              // eslint-disable-next-line sonarjs/cognitive-complexity
            }) => [
              ...(selectedItems.length > 0 && hasRoles([Role.Administrador])
                ? handleSelectedItemsAction(
                    selectedItems,
                    isAllSelected,
                    reloadData,
                  )
                : isAllSelected && hasRoles([Role.Administrador])
                ? handleSelectedItemsAction(
                    selectedItems,
                    isAllSelected,
                    reloadData,
                  )
                : handleNotSelected()),
            ]}
          />
        </DataTableWrapper>
      </ContentGrid>
    </BaSeContainer>
  );
};
